'use client';

import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import { AlertTriangle, FileText, Loader2, Sparkles } from 'lucide-react';

import { useRouter } from '@/i18n/routing';
import { toast } from 'sonner';
import { useRouteAccess } from '@/hooks/use-route-access';
import { cn } from '@/lib/utils';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator
} from '@/components/ui/command';
import { searchQueries } from '../services/search.queries';
import { hitPath, purifySearchSnippet } from '../utils';
import type { SearchHit } from '../types';

interface GlobalSearchDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/**
 * Cmd+K (Ctrl+K) global search. Debounces input by 300ms before firing
 * the backend query so quick typists don't burn a roundtrip per keystroke.
 * Results are grouped by the registration_id they belong to so a query
 * like a container number returns one section showing the container's
 * full journey across pipeline stages (OMA-548 AC #7).
 *
 * `shouldFilter={false}` on the Command — cmdk's built-in fuzzy filter
 * would otherwise hide rows whose item value doesn't contain the typed
 * query, but the backend already returned the authoritative match set.
 */
export function GlobalSearchDialog({
  open,
  onOpenChange
}: GlobalSearchDialogProps) {
  const t = useTranslations('search');
  const tg = useTranslations('auth.guard');
  const router = useRouter();
  const canAccess = useRouteAccess();
  const [input, setInput] = useState('');
  const [debounced, setDebounced] = useState('');

  // Reset in the close handler instead of an effect — avoids the
  // set-state-in-effect cascade lint and matches React's "derive in events,
  // not effects" guidance.
  const handleOpenChange = (next: boolean) => {
    if (!next) {
      setInput('');
      setDebounced('');
    }
    onOpenChange(next);
  };

  useEffect(() => {
    const id = setTimeout(() => setDebounced(input.trim()), 300);
    return () => clearTimeout(id);
  }, [input]);

  const enabled = debounced.length > 0;
  const { data, isFetching } = useQuery(
    searchQueries.query({ q: debounced, per_page: 25 }, enabled)
  );

  const grouped = useMemo(() => groupHits(data?.hits ?? []), [data?.hits]);

  const navigateToHit = (hit: SearchHit) => {
    const path = hitPath(hit);
    if (!path) return;
    // Search spans every module, so a hit routinely names a screen this account
    // cannot open. Say so and leave the palette up rather than navigating into
    // a full-page "Access Denied".
    if (!canAccess(path)) {
      toast.error(tg('noPermission'));
      return;
    }
    onOpenChange(false);
    router.push(path);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogHeader className='sr-only'>
        <DialogTitle>{t('title')}</DialogTitle>
        <DialogDescription>{t('description')}</DialogDescription>
      </DialogHeader>
      <DialogContent className='overflow-hidden p-0'>
        <Command shouldFilter={false}>
          <CommandInput
            value={input}
            onValueChange={setInput}
            placeholder={t('placeholder')}
          />
          <CommandList>
            {data?.degraded && (
              <div
                className={cn(
                  'mx-2 my-2 flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-700 dark:text-amber-300'
                )}
              >
                <AlertTriangle className='mt-0.5 h-3.5 w-3.5 shrink-0' />
                <span>{t('degradedBanner')}</span>
              </div>
            )}

            {isFetching && enabled && (
              <div className='text-muted-foreground flex items-center justify-center gap-2 px-3 py-6 text-sm'>
                <Loader2 className='h-3.5 w-3.5 animate-spin' />
                <span>{t('searching')}</span>
              </div>
            )}

            {!isFetching && enabled && (data?.hits.length ?? 0) === 0 && (
              <CommandEmpty>{t('noResults')}</CommandEmpty>
            )}

            {!enabled && (
              <div className='text-muted-foreground px-3 py-6 text-center text-xs'>
                {t('emptyPrompt')}
              </div>
            )}

            {enabled && (
              <CommandGroup>
                <CommandItem
                  value={`__smart__:${debounced}`}
                  onSelect={() => {
                    onOpenChange(false);
                    router.push(
                      `/dashboard/search?q=${encodeURIComponent(debounced)}`
                    );
                  }}
                  className='gap-2'
                >
                  <Sparkles className='text-primary h-3.5 w-3.5 shrink-0' />
                  <span className='truncate text-sm'>
                    {t('smartSearchFor', { query: debounced })}
                  </span>
                </CommandItem>
              </CommandGroup>
            )}

            {grouped.map((group, idx) => (
              <CommandGroup
                key={group.key}
                heading={
                  group.registrationId !== null
                    ? t('groupRegistration', { id: group.registrationId })
                    : t('groupOther')
                }
              >
                {group.hits.map((hit) => (
                  <CommandItem
                    key={`${hit.entity_type}:${hit.entity_id}`}
                    value={`${hit.entity_type}:${hit.entity_id}:${hit.title ?? ''}`}
                    onSelect={() => navigateToHit(hit)}
                    className='gap-2'
                  >
                    <FileText className='text-muted-foreground h-3.5 w-3.5 shrink-0' />
                    <div className='flex min-w-0 flex-1 flex-col'>
                      <span className='truncate text-sm'>
                        {hit.title ?? hit.identifiers?.[0] ?? '—'}
                      </span>
                      {hit.snippet && (
                        <span
                          className='text-muted-foreground truncate text-xs'
                          // Typesense wraps the match in <mark>; everything
                          // around it is OCR text from an uploaded document, so
                          // it is escaped before the tags are re-admitted, then
                          // re-parsed by DOMPurify.
                          dangerouslySetInnerHTML={{
                            __html: purifySearchSnippet(hit.snippet)
                          }}
                        />
                      )}
                    </div>
                    <span className='text-muted-foreground shrink-0 text-[10px] uppercase'>
                      {hit.entity_type ?? ''}
                    </span>
                  </CommandItem>
                ))}
                {idx < grouped.length - 1 && <CommandSeparator />}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </DialogContent>
    </Dialog>
  );
}

interface HitGroup {
  key: string;
  registrationId: number | null;
  hits: SearchHit[];
}

function groupHits(hits: SearchHit[]): HitGroup[] {
  const map = new Map<string, HitGroup>();
  for (const hit of hits) {
    const id = hit.registration_id ?? null;
    const key = id !== null ? `reg:${id}` : 'other';
    if (!map.has(key)) {
      map.set(key, { key, registrationId: id, hits: [] });
    }
    map.get(key)!.hits.push(hit);
  }
  return Array.from(map.values());
}
